/**
 * eSocial Doméstico — Fase 13
 * Cadastro de empregadores (PF/CPF) + empregados domésticos + eventos eSocial específicos
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { CurrencyInput } from '../ui/currency-input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Home, Plus, Loader2, Users, FileCode, Download, FileSignature, Trash2, CheckCheck,
  Edit3, RefreshCw, User,
} from 'lucide-react';
import { useConfirm } from '../../hooks/useConfirm';
import { formatCPF, formatPhone, onlyNumbers, validateCPF, validateDateBR } from '../../lib/helpers';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const TIPOS_D = {
  'S-1000-D': 'Cadastro Empregador',
  'S-2200-D': 'Admissão',
  'S-1200-D': 'Remuneração',
  'S-1210-D': 'Pagamento',
  'S-2299-D': 'Desligamento',
};

const STATUS_BADGE = {
  rascunho: 'bg-gray-100 text-gray-700',
  assinado: 'bg-blue-100 text-blue-700',
  transmitido: 'bg-amber-100 text-amber-700',
  processado_ok: 'bg-emerald-100 text-emerald-700',
  rejeitado: 'bg-red-100 text-red-700',
};

const KpiPanel = ({ empregadorId, refreshKey }) => {
  const [k, setK] = useState({ total_eventos: 0, empregadores: 0, empregados: 0, por_status: {} });
  useEffect(() => {
    axios.get(`${API}/api/esocial-domestico/kpis`,
      { ...auth(), params: empregadorId ? { empregador_id: empregadorId } : {} })
      .then(r => setK(r.data)).catch(() => {});
  }, [empregadorId, refreshKey]);
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="dom-kpis">
      <div className="border rounded-lg p-3 bg-slate-50"><div className="text-xs">Empregadores</div><div className="text-2xl font-bold">{k.empregadores}</div></div>
      <div className="border rounded-lg p-3 bg-indigo-50"><div className="text-xs">Empregados</div><div className="text-2xl font-bold">{k.empregados}</div></div>
      <div className="border rounded-lg p-3 bg-gray-50"><div className="text-xs">Rascunhos</div><div className="text-2xl font-bold">{k.por_status?.rascunho || 0}</div></div>
      <div className="border rounded-lg p-3 bg-blue-50"><div className="text-xs">Assinados</div><div className="text-2xl font-bold">{k.por_status?.assinado || 0}</div></div>
      <div className="border rounded-lg p-3 bg-emerald-50"><div className="text-xs">Total Eventos</div><div className="text-2xl font-bold">{k.total_eventos}</div></div>
    </div>
  );
};

const EmpregadoresTab = ({ onSelect, empregadorSel }) => {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ cpf: '', nome_completo: '', email: '', telefone: '' });
  const [editingId, setEditingId] = useState(null);
  const { confirm, ConfirmDialog } = useConfirm();

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/api/esocial-domestico/empregadores`, auth());
    setList(r.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const cpfDigits = onlyNumbers(form.cpf);
    if (!validateCPF(cpfDigits)) return toast.error('CPF inválido');
    if (!form.nome_completo) return toast.error('Nome é obrigatório');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return toast.error('E-mail inválido');
    const phoneDigits = onlyNumbers(form.telefone);
    if (phoneDigits && (phoneDigits.length < 10 || phoneDigits.length > 11)) return toast.error('Telefone deve ter 10 ou 11 dígitos (DDD + número)');
    try {
      const body = { ...form, cpf: cpfDigits, telefone: phoneDigits };
      if (editingId) {
        await axios.put(`${API}/api/esocial-domestico/empregadores/${editingId}`, body, auth());
        toast.success('Empregador atualizado');
      } else {
        await axios.post(`${API}/api/esocial-domestico/empregadores`, body, auth());
        toast.success('Empregador cadastrado');
      }
      setOpen(false); setEditingId(null); setForm({ cpf: '', nome_completo: '', email: '', telefone: '' }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
  };

  const remover = async (e) => {
    const ok = await confirm({ title: 'Remover empregador?', description: e.nome_completo });
    if (!ok) return;
    try {
      await axios.delete(`${API}/api/esocial-domestico/empregadores/${e.id}`, auth());
      toast.success('Removido'); load();
    } catch (err) { toast.error(err?.response?.data?.detail); }
  };

  const editar = (emp) => {
    setForm({ cpf: formatCPF(emp.cpf), nome_completo: emp.nome_completo, email: emp.email, telefone: emp.telefone });
    setEditingId(emp.id);
    setOpen(true);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2"><Home className="h-5 w-5 text-indigo-600" />Empregadores Domésticos</CardTitle>
          <CardDescription>Pessoa Física empregadora (CPF, classTrib=22)</CardDescription>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="dom-empregador-novo-btn"><Plus className="h-4 w-4 mr-1" />Novo</Button>
      </CardHeader>
      <CardContent>
        <table className="w-full text-sm">
          <thead className="text-left text-slate-500 border-b">
            <tr><th className="py-2">Nome</th><th>CPF</th><th>Email</th><th className="w-44">Ações</th></tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-slate-400">Nenhum empregador cadastrado</td></tr>}
            {list.map(e => (
              <tr key={e.id} className="border-b">
                <td className="py-2">{e.nome_completo}</td>
                <td className="font-mono text-xs">{formatCPF(e.cpf)}</td>
                <td className="text-xs text-slate-600">{e.email}</td>
                <td className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => onSelect(e)}
                          data-testid={`dom-empregador-select-${e.id}`}
                          className={e.id === empregadorSel?.id ? 'text-green-600 border-green-400' : ''}>
                    {e.id === empregadorSel?.id ? <>Selecionado <CheckCheck className="h-4 w-4" /></> : 'Selecionar'}</Button>
                  <Button size="sm" variant="ghost" onClick={() => editar(e)}><Edit3 className="h-3.5 w-3.5 text-blue-600" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remover(e)}><Trash2 className="h-3.5 w-3.5 text-red-600" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditingId(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar Empregador Doméstico' : 'Novo Empregador Doméstico'}</DialogTitle>
            <DialogDescription>Pessoa Física empregadora (CLT-Doméstico)</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>CPF (11 dígitos)</Label><Input value={form.cpf} onChange={e => setForm({ ...form, cpf: formatCPF(e.target.value) })} maxLength={14} data-testid="dom-empregador-cpf-input" /></div>
            <div><Label>Nome Completo</Label><Input value={form.nome_completo} onChange={e => setForm({ ...form, nome_completo: e.target.value.replace(/\d/g, '') })} /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Telefone</Label><Input value={formatPhone(form.telefone)} onChange={e => setForm({ ...form, telefone: e.target.value })} /></div>
            <Button onClick={save} className="w-full" data-testid="dom-empregador-save-btn"><Plus className="h-4 w-4 mr-1" />{editingId ? 'Atualizar' : 'Cadastrar'}</Button>
          </div>
        </DialogContent>
      </Dialog>
      <ConfirmDialog />
    </Card>
  );
};

const EmpregadosTab = ({ empregador, onChange }) => {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    cpf: '', nome_completo: '', sexo: 'F', raca_cor: '3',
    estado_civil: '1', grau_instr: '06', nascimento_dt: '',
    cbo: '513110', matricula: '', data_admissao: '', vlr_salario: 1518.00,
    qtd_hrs_sem: 44.0,
  });
  const [editingId, setEditingId] = useState(null);
  const { confirm, ConfirmDialog } = useConfirm();

  const load = useCallback(async () => {
    if (!empregador) return setList([]);
    const r = await axios.get(`${API}/api/esocial-domestico/empregados`,
      { ...auth(), params: { empregador_id: empregador.id } });
    setList(r.data || []);
  }, [empregador]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.cpf.replace(/\D/g, '') || !form.nome_completo || !form.nascimento_dt || !form.data_admissao || !form.matricula)
      return toast.error('Preencha todos os campos obrigatórios');
    if (!empregador) return toast.error('Selecione um empregador');
    const cpfDigits = onlyNumbers(form.cpf);
    if (!validateCPF(cpfDigits)) return toast.error('CPF inválido');
    if (!validateDateBR(form.nascimento_dt, { futureYears: 0, pastYears: 120 })) return toast.error('Data de nascimento inválida');
    if (!validateDateBR(form.data_admissao, { pastYears: 15, futureYears: 15 })) return toast.error('Data de admissão inválida');
    try {
      const body = { ...form, cpf: cpfDigits, empregador_id: empregador.id, vlr_salario: form.vlr_salario, qtd_hrs_sem: parseFloat(form.qtd_hrs_sem) };
      if (editingId) {
        await axios.put(`${API}/api/esocial-domestico/empregados/${editingId}`, body, auth());
        toast.success('Empregado atualizado');
      } else {
        await axios.post(`${API}/api/esocial-domestico/empregados`, body, auth());
        toast.success('Empregado cadastrado');
      }
      setOpen(false); setEditingId(null); load(); if (onChange) onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
  };

  const editar = (emp) => {
    setForm({ ...emp, cpf: formatCPF(emp.cpf), qtd_hrs_sem: String(emp.qtd_hrs_sem) });
    setEditingId(emp.id);
    setOpen(true);
  };

  const remover = async (emp) => {
    const ok = await confirm({ title: 'Remover empregado?', description: emp.nome_completo });
    if (!ok) return;
    try {
      await axios.delete(`${API}/api/esocial-domestico/empregados/${emp.id}`, auth());
      toast.success('Empregado removido');
      load(); if (onChange) onChange();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erro'); }
  };

  const gerarEvento = async (empregado, tipo) => {
    try {
      let url, body;
      if (tipo === 'S-2200') { url = '/api/esocial-domestico/s2200/gerar'; body = { empregado_id: empregado.id }; }
      else if (tipo === 'S-1200') { url = '/api/esocial-domestico/s1200/gerar'; body = { empregado_id: empregado.id, per_apur: new Date().toISOString().slice(0, 7) }; }
      else if (tipo === 'S-1210') { url = '/api/esocial-domestico/s1210/gerar'; body = { empregado_id: empregado.id, per_apur: new Date().toISOString().slice(0, 7), dt_pgto: new Date().toISOString().slice(0, 10), vr_liq: empregado.vlr_salario }; }
      else if (tipo === 'S-2299') {
        const dt = prompt('Data desligamento (AAAA-MM-DD):'); if (!dt) return;
        url = '/api/esocial-domestico/s2299/gerar'; body = { empregado_id: empregado.id, dt_desligamento: dt, motivo: '02' };
      }
      const r = await axios.post(`${API}${url}`, body, auth());
      toast.success(`✅ ${tipo} gerado — ${r.data.id_esocial.slice(0, 14)}…`);
      if (onChange) onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-indigo-600" />Empregados de {empregador?.nome_completo || '—'}</CardTitle>
          <CardDescription>Vínculos CLT-Doméstico (codCateg=104)</CardDescription>
        </div>
        <Button onClick={() => setOpen(true)} disabled={!empregador} data-testid="dom-empregado-novo-btn"><Plus className="h-4 w-4 mr-1" />Novo</Button>
      </CardHeader>
      <CardContent>
        <table className="w-full text-sm">
          <thead className="text-left text-slate-500 border-b">
            <tr><th className="py-2">Nome</th><th>CPF</th><th>Matrícula</th><th>Salário</th><th>Gerar Evento</th><th className="w-28">Ações</th></tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-slate-400">{empregador ? 'Nenhum empregado' : 'Selecione um empregador'}</td></tr>}
            {list.map(e => (
              <tr key={e.id} className="border-b">
                <td className="py-2"><User className="inline h-3.5 w-3.5 mr-1" />{e.nome_completo}</td>
                <td className="font-mono text-xs">{formatCPF(e.cpf)}</td>
                <td className="text-xs">{e.matricula}</td>
                <td>R$ {Number(e.vlr_salario || 0).toFixed(2)}</td>
                <td className="flex flex-wrap gap-1">
                  {['S-2200', 'S-1200', 'S-1210', 'S-2299'].map(t => (
                    <Button key={t} size="sm" variant="outline" onClick={() => gerarEvento(e, t)}
                            className="text-xs px-2"
                            data-testid={`dom-evento-${t.toLowerCase()}-btn-${e.id}`}>{t}</Button>
                  ))}
                </td>
                <td>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => editar(e)}><Edit3 className="h-3.5 w-3.5 text-blue-600" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => remover(e)}><Trash2 className="h-3.5 w-3.5 text-red-600" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditingId(null); }}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{editingId ? 'Editar Empregado Doméstico' : 'Novo Empregado Doméstico'}</DialogTitle><DialogDescription>CLT regime doméstico (codCateg=104)</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>CPF</Label><Input value={form.cpf} onChange={e => setForm({ ...form, cpf: formatCPF(e.target.value) })} maxLength={14} data-testid="dom-empregado-cpf-input" /></div>
            <div><Label>Nome Completo</Label><Input value={form.nome_completo} onChange={e => setForm({ ...form, nome_completo: e.target.value.replace(/\d/g, '') })} /></div>
            <div><Label>Data Nascimento</Label><Input type="date" value={form.nascimento_dt} onChange={e => setForm({ ...form, nascimento_dt: e.target.value })} /></div>
            <div><Label>Data Admissão</Label><Input type="date" value={form.data_admissao} onChange={e => setForm({ ...form, data_admissao: e.target.value })} /></div>
            <div><Label>Matrícula</Label><Input value={form.matricula} onChange={e => setForm({ ...form, matricula: e.target.value })} placeholder="DOM0001" /></div>
            <div><Label>CBO</Label><Input value={form.cbo} onChange={e => setForm({ ...form, cbo: e.target.value })} /></div>
            <div><Label>Salário (R$)</Label><CurrencyInput value={form.vlr_salario} onChange={v => setForm({ ...form, vlr_salario: v })} /></div>
            <div><Label>Horas Semanais</Label><Input type="number" step="0.5" value={form.qtd_hrs_sem} onChange={e => setForm({ ...form, qtd_hrs_sem: e.target.value })} /></div>
            <div>
              <Label>Sexo</Label>
              <Select value={form.sexo} onValueChange={v => setForm({ ...form, sexo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="F">Feminino</SelectItem><SelectItem value="M">Masculino</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label>Raça/Cor</Label>
              <Select value={form.raca_cor} onValueChange={v => setForm({ ...form, raca_cor: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Branca</SelectItem><SelectItem value="2">Preta</SelectItem>
                  <SelectItem value="3">Parda</SelectItem><SelectItem value="4">Amarela</SelectItem>
                  <SelectItem value="5">Indígena</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={save} className="w-full mt-3" data-testid="dom-empregado-save-btn"><Plus className="h-4 w-4 mr-1" />{editingId ? 'Atualizar' : 'Cadastrar'}</Button>
        </DialogContent>
      </Dialog>
      <ConfirmDialog />
    </Card>
  );
};

const EventosTab = ({ empregador, refreshKey, onChange }) => {
  const [eventos, setEventos] = useState([]);
  const [busy, setBusy] = useState(null);
  const { confirm, ConfirmDialog } = useConfirm();

  const load = useCallback(async () => {
    if (!empregador) return setEventos([]);
    const r = await axios.get(`${API}/api/esocial-domestico/eventos`,
      { ...auth(), params: { empregador_id: empregador.id, limit: 200 } });
    setEventos(r.data || []);
  }, [empregador]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const gerarS1000 = async () => {
    if (!empregador) return;
    try {
      const r = await axios.post(`${API}/api/esocial-domestico/s1000/gerar`,
        { empregador_id: empregador.id, ini_valid: new Date().toISOString().slice(0, 7) }, auth());
      toast.success(`S-1000-D gerado — ${r.data.id_esocial.slice(0, 14)}…`);
      load(); if (onChange) onChange();
    } catch (e) { toast.error(e?.response?.data?.detail); }
  };

  const acao = async (ev, op) => {
    setBusy(ev.id);
    try {
      if (op === 'assinar') {
        await axios.post(`${API}/api/esocial-domestico/eventos/${ev.id}/assinar`, {}, auth());
        toast.success('Assinado');
      } else if (op === 'xml') {
        const r = await axios.get(`${API}/api/esocial-domestico/eventos/${ev.id}/xml`, auth());
        const blob = new Blob([r.data], { type: 'application/xml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `${ev.tipo}_${ev.id_esocial}.xml`;
        a.click(); URL.revokeObjectURL(url);
      } else if (op === 'excluir') {
        const ok = await confirm({ title: 'Excluir evento?', description: `${ev.tipo} — ${ev.id_esocial?.slice(0, 14)}…` });
        if (!ok) return;
        await axios.delete(`${API}/api/esocial-domestico/eventos/${ev.id}`, auth());
        toast.success('Removido');
      }
      load(); if (onChange) onChange();
    } catch (e) { toast.error(e?.response?.data?.detail); }
    finally { setBusy(null); }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2"><FileCode className="h-5 w-5 text-indigo-600" />Eventos eSocial Doméstico</CardTitle>
          <CardDescription>{empregador ? `Eventos de ${empregador.nome_completo}` : 'Selecione um empregador'}</CardDescription>
        </div>
        <div className="flex gap-2">
          {empregador && <Button size="sm" onClick={gerarS1000} data-testid="dom-s1000-btn"><Plus className="h-4 w-4 mr-1" />S-1000-D</Button>}
          <Button size="sm" variant="ghost" onClick={load}><RefreshCw className="h-3.5 w-3.5" /></Button>
        </div>
      </CardHeader>
      <CardContent>
        <table className="w-full text-sm">
          <thead className="text-left text-slate-500 border-b">
            <tr><th className="py-2">Tipo</th><th>ID</th><th>Empregado</th><th>Status</th><th>Criado</th><th className="w-40">Ações</th></tr>
          </thead>
          <tbody>
            {eventos.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-slate-400">Nenhum evento</td></tr>}
            {eventos.map(ev => (
              <tr key={ev.id} className="border-b">
                <td className="py-2"><Badge variant="outline">{ev.tipo}</Badge><div className="text-xs text-slate-400">{TIPOS_D[ev.tipo] || ''}</div></td>
                <td className="font-mono text-xs">{ev.id_esocial?.slice(0, 14)}…</td>
                <td className="text-xs">{ev.cpf_empregado ? formatCPF(ev.cpf_empregado) : '-'}</td>
                <td><Badge className={STATUS_BADGE[ev.status] || ''}>{ev.status}</Badge></td>
                <td className="text-xs text-slate-500">{(ev.criado_em || '').slice(0, 16).replace('T', ' ')}</td>
                <td className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => acao(ev, 'xml')}><Download className="h-3.5 w-3.5" /></Button>
                  {ev.status === 'rascunho' && (
                    <>
                      <Button size="sm" variant="ghost" disabled={busy === ev.id} onClick={() => acao(ev, 'assinar')}><FileSignature className="h-3.5 w-3.5 text-blue-600" /></Button>
                      <Button size="sm" variant="ghost" disabled={busy === ev.id} onClick={() => acao(ev, 'excluir')}><Trash2 className="h-3.5 w-3.5 text-red-600" /></Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
      <ConfirmDialog />
    </Card>
  );
};

export default function EsocialDomesticoPage() {
  const [empregadorSel, setEmpregadorSel] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <div className="p-6 space-y-4 max-w-7xl mx-auto" data-testid="dom-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Home className="h-7 w-7 text-indigo-600" />eSocial Doméstico <Badge variant="outline" className="ml-2">Fase 13</Badge></h1>
          <p className="text-sm text-slate-500">CLT Doméstico — empregador pessoa física (CPF, classTrib=22)</p>
        </div>
        {empregadorSel && (
          <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-300" data-testid="dom-empregador-selecionado">
            Empregador ativo: {empregadorSel.nome_completo}
          </Badge>
        )}
      </div>
      <KpiPanel empregadorId={empregadorSel?.id} refreshKey={refreshKey} />
      <Tabs defaultValue="empregadores">
        <TabsList>
          <TabsTrigger value="empregadores" data-testid="dom-tab-empregadores">Empregadores</TabsTrigger>
          <TabsTrigger value="empregados" data-testid="dom-tab-empregados">Empregados</TabsTrigger>
          <TabsTrigger value="eventos" data-testid="dom-tab-eventos">Eventos</TabsTrigger>
        </TabsList>
        <TabsContent value="empregadores" className="mt-4"><EmpregadoresTab empregadorSel={empregadorSel} onSelect={(e) => { setEmpregadorSel(e); toast.info(`Empregador selecionado: ${e.nome_completo}`); }} /></TabsContent>
        <TabsContent value="empregados" className="mt-4"><EmpregadosTab empregador={empregadorSel} onChange={() => setRefreshKey(k => k + 1)} /></TabsContent>
        <TabsContent value="eventos" className="mt-4"><EventosTab empregador={empregadorSel} refreshKey={refreshKey} onChange={() => setRefreshKey(k => k + 1)} /></TabsContent>
      </Tabs>
    </div>
  );
}
